import SearchBoxLayout from "@/components/layout/SearchBoxLayout";
import AddModal from "@/components/modal/standard/commonCode/Add";
import UpdateModal from "@/components/modal/standard/commonCode/Update";
import { Button } from "@/components/ui/Button";
import { PermissionButton } from "@/components/ui/PermissionButton";
import CountBox from "@/components/ui/CountBox";
import { InputBox } from "@/components/ui/InputBox";
import Table, { TableColumn } from "@/components/ui/Table";
import { useCodeDelete, useCodeList, useCodeNameSearch, useInfiniteScrollPage } from "@/hooks";
import { useState } from "react";
import { toast } from "sonner";
import SelectBox from "@/components/ui/SelectBox";

export default function CommonCodePage() {
  const [searchType, setSearchType] = useState<"parent" | "child">("parent"); // 검색 타입 (대분류/소분류)
  const [searchValue, setSearchValue] = useState(""); // 검색어
  const [applied, setApplied] = useState(false); // 적용 여부 (적용: 검색 결과, 미적용: 전체 목록)
  const [appliedType, setAppliedType] = useState<"parent" | "child">("parent"); // 적용된 검색 타입
  const [appliedResults, setAppliedResults] = useState<any[]>([]); // 적용된 검색 결과

  // 각 depth별 선택 상태
  const [selectedRows1, setSelectedRows1] = useState<number[]>([]); // 1depth 체크박스
  const [selectedRows2, setSelectedRows2] = useState<number[]>([]); // 2depth 체크박스
  const [selectedRows3, setSelectedRows3] = useState<number[]>([]); // 3depth 체크박스
  const [selectedRows4, setSelectedRows4] = useState<number[]>([]); // 4depth 체크박스

  const [selectedRow1, setSelectedRow1] = useState<any>(null); // 1depth 선택 행
  const [selectedRow2, setSelectedRow2] = useState<any>(null); // 2depth 선택 행
  const [selectedRow3, setSelectedRow3] = useState<any>(null); // 3depth 선택 행

  const [addModalOpen, setAddModalOpen] = useState(false); // 추가 모달
  const [updateModalOpen, setUpdateModalOpen] = useState(false); // 수정 모달
  const [selectedRowForUpdate, setSelectedRowForUpdate] = useState<any>(null); // 수정 모달용 선택 데이터

  const [parentId1, setParentId1] = useState<number | undefined>(undefined); // 2depth 조회용
  const [parentId2, setParentId2] = useState<number | undefined>(undefined); // 3depth 조회용
  const [parentId3, setParentId3] = useState<number | undefined>(undefined); // 4depth 조회용

  const [table1Ver, setTable1Ver] = useState(0);
  const [table2Ver, setTable2Ver] = useState(0);
  const [table3Ver, setTable3Ver] = useState(0);
  const [table4Ver, setTable4Ver] = useState(0);
  const [tablesVer, setTablesVer] = useState(0);

  // 1depth 데이터 조회
  const {
    data: data1,
    isLoading: loading1,
    refetch: refetch1,
  } = useCodeList({
    parentId: undefined,
  });

  // 2depth 데이터 조회
  const {
    data: data2,
    isLoading: loading2,
    refetch: refetch2,
  } = useCodeList({
    parentId: parentId1,
  });

  // 3depth 데이터 조회
  const {
    data: data3,
    isLoading: loading3,
    refetch: refetch3,
  } = useCodeList({
    parentId: parentId2,
  });

  // 4depth 데이터 조회
  const {
    data: data4,
    isLoading: loading4,
    refetch: refetch4,
  } = useCodeList({
    parentId: parentId3,
  });

  // 검색 결과 상태
  const [searchResults, setSearchResults] = useState<any[]>([]);
  const [isSearchMode, setIsSearchMode] = useState(false);

  // 코드명 검색 훅
  const { mutate: searchCode, isPending: searchLoading } = useCodeNameSearch(searchValue || "");
  // 코드 삭제 훅
  const { mutate: deleteCode } = useCodeDelete();

  // 삭제 버튼
  const handlerDeleteCode = () => {
    // 4개 테이블에서 선택된 모든 id 수집
    const ids1 = selectedRows1.map(i => data1?.[i]?.codeId).filter((v): v is number => v != null);
    const ids2 = selectedRows2.map(i => data2?.[i]?.codeId).filter((v): v is number => v != null);
    const ids3 = selectedRows3.map(i => data3?.[i]?.codeId).filter((v): v is number => v != null);
    const ids4 = selectedRows4.map(i => data4?.[i]?.codeId).filter((v): v is number => v != null);

    const idsToDelete = [...ids1, ...ids2, ...ids3, ...ids4];

    if (idsToDelete.length === 0) {
      return toast.warning("삭제할 항목을 선택하세요.");
    }

    deleteCode(idsToDelete, {
      onSuccess: () => {
        // 선택 상태 초기화
        setSelectedRow1(null);
        setSelectedRow2(null);
        setSelectedRow3(null);
        setSelectedRows1([]);
        setSelectedRows2([]);
        setSelectedRows3([]);
        setSelectedRows4([]);
        setParentId1(undefined);
        setParentId2(undefined);
        setParentId3(undefined);
        setTable1Ver(v => v + 1);
        setTable2Ver(v => v + 1);
        setTable3Ver(v => v + 1);
        setTable4Ver(v => v + 1);

        // 서버와 동기화
        refetch1();
        refetch2();
        refetch3();
        refetch4();
      },
      onError: () => {
        toast.error("삭제 실패");
      },
    });
  };

  // 초기화 버튼
  const handleReset = () => {
    setSearchType("parent");
    setSearchValue("");
    setApplied(false);
    setAppliedResults([]);
    setAppliedType("parent");
    setSelectedRow1(null);
    setSelectedRow2(null);
    setSelectedRow3(null);
    setSelectedRows1([]);
    setSelectedRows2([]);
    setSelectedRows3([]);
    setSelectedRows4([]);
    setParentId1(undefined);
    setParentId2(undefined);
    setParentId3(undefined);
    resetSearchMode();
    setTable1Ver(v => v + 1);
    setTable2Ver(v => v + 1);
    setTable3Ver(v => v + 1);
    setTable4Ver(v => v + 1);
  };

  // 조회 버튼 - 서버 사이드 검색
  const handleSearch = () => {
    const q = (searchValue ?? "").trim();

    // 검색어가 없으면 검색 모드 해제
    if (q === "") {
      resetSearchMode();
      return;
    }

    searchCode(q, {
      onSuccess: (res: any) => {
        console.log("🔍 검색 응답:", res);
        console.log("🔍 검색 응답 데이터:", res?.data);

        if (res?.code === 200 && res?.data) {
          const results = Array.isArray(res.data) ? res.data : [];
          console.log("🔍 필터링된 결과:", results);
          setSearchResults(results);
          setIsSearchMode(true);
          // 테이블 강제 리렌더링
          setTable1Ver(v => v + 1);
          setTable2Ver(v => v + 1);
          setTable3Ver(v => v + 1);
          setTable4Ver(v => v + 1);
          toast.success(`${results.length}개의 항목을 찾았습니다.`);
        } else {
          toast.info("검색 결과가 없습니다.");
          setSearchResults([]);
          setIsSearchMode(false);
        }
      },
      onError: (err: any) => {
        console.error("🔍 검색 에러:", err);
        toast.error("검색에 실패했습니다.");
        setSearchResults([]);
        setIsSearchMode(false);
      },
    });
  };

  // 검색 모드 초기화
  const resetSearchMode = () => {
    setIsSearchMode(false);
    setSearchResults([]);
    setSearchValue("");
    // 테이블 강제 리렌더링
    setTable1Ver(v => v + 1);
    setTable2Ver(v => v + 1);
    setTable3Ver(v => v + 1);
    setTable4Ver(v => v + 1);
  };

  // 1depth 테이블 선택 핸들러
  const handle1RowClick = (row: any) => {
    setSelectedRow1(row);
    setParentId1(row.codeId);
    setSelectedRows2([]);
    setSelectedRow2(null);
    setParentId2(undefined);
    setSelectedRows3([]);
    setSelectedRow3(null);
    setParentId3(undefined);
    setSelectedRows4([]);
  };

  // 2depth 테이블 선택 핸들러
  const handle2RowClick = (row: any) => {
    setSelectedRow2(row);
    setParentId2(row.codeId);
    setSelectedRows3([]);
    setSelectedRow3(null);
    setParentId3(undefined);
    setSelectedRows4([]);
  };

  // 3depth 테이블 선택 핸들러
  const handle3RowClick = (row: any) => {
    setSelectedRow3(row);
    setParentId3(row.codeId);
    setSelectedRows4([]);
  };

  // 수정 버튼 클릭 시 선택된 행 데이터 설정
  const handleUpdateClick = () => {
    const totalSelected = selectedRows1.length + selectedRows2.length + selectedRows3.length + selectedRows4.length;

    if (totalSelected !== 1) {
      return toast.warning("수정할 행을 1건만 선택하세요.");
    }

    let rowData: any = null;

    if (selectedRows1.length === 1) {
      rowData = data1?.[selectedRows1[0]];
    } else if (selectedRows2.length === 1) {
      rowData = data2?.[selectedRows2[0]];
    } else if (selectedRows3.length === 1) {
      rowData = data3?.[selectedRows3[0]];
    } else if (selectedRows4.length === 1) {
      rowData = data4?.[selectedRows4[0]];
    }

    if (!rowData) {
      return toast.error("데이터를 찾을 수 없습니다.");
    }

    setSelectedRowForUpdate(rowData);
    setUpdateModalOpen(true);
  };

  const columns1: TableColumn[] = [
    { key: "code", header: "코드", bodyAlign: "left", group: "1depth" },
    { key: "name", header: "명칭", bodyAlign: "left", group: "1depth" },
    { key: "sortOrder", header: "정렬순서", group: "1depth" },
    { key: "useYn", header: "사용여부", group: "1depth" },
    { key: "remark", header: "비고", bodyAlign: "left", group: "1depth" },
  ];

  const columns2: TableColumn[] = [
    { key: "code", header: "코드", bodyAlign: "left", group: "2depth" },
    { key: "name", header: "명칭", bodyAlign: "left", group: "2depth" },
    { key: "sortOrder", header: "정렬순서", group: "2depth" },
    { key: "useYn", header: "사용여부", group: "2depth" },
    { key: "remark", header: "비고", bodyAlign: "left", group: "2depth" },
  ];

  const columns3: TableColumn[] = [
    { key: "code", header: "코드", bodyAlign: "left", group: "3depth" },
    { key: "name", header: "명칭", bodyAlign: "left", group: "3depth" },
    { key: "sortOrder", header: "정렬순서", group: "3depth" },
    { key: "useYn", header: "사용여부", group: "3depth" },
    { key: "remark", header: "비고", bodyAlign: "left", group: "3depth" },
  ];

  const columns4: TableColumn[] = [
    { key: "code", header: "코드", bodyAlign: "left", group: "4depth" },
    { key: "name", header: "명칭", bodyAlign: "left", group: "4depth" },
    { key: "sortOrder", header: "정렬순서", group: "4depth" },
    { key: "useYn", header: "사용여부", group: "4depth" },
    { key: "remark", header: "비고", bodyAlign: "left", group: "4depth" },
  ];

  // 각 테이블 데이터 - 검색 모드일 때는 1depth 테이블에만 검색 결과를 표시
  const safeData1 = isSearchMode ? searchResults : data1 || [];
  const safeData2 = isSearchMode ? [] : parentId1 !== undefined && Array.isArray(data2) ? data2 : [];
  const safeData3 = isSearchMode ? [] : parentId2 !== undefined && Array.isArray(data3) ? data3 : [];
  const safeData4 = isSearchMode ? [] : parentId3 !== undefined && Array.isArray(data4) ? data4 : [];

  // 각 테이블 무한스크롤
  const scroll1 = useInfiniteScrollPage({
    data: safeData1,
    initialLoadCount: 20,
    loadMoreCount: 20,
  });

  const scroll2 = useInfiniteScrollPage({
    data: safeData2,
    initialLoadCount: 20,
    loadMoreCount: 20,
  });

  const scroll3 = useInfiniteScrollPage({
    data: safeData3,
    initialLoadCount: 20,
    loadMoreCount: 20,
  });

  const scroll4 = useInfiniteScrollPage({
    data: safeData4,
    initialLoadCount: 20,
    loadMoreCount: 20,
  });

  // 수정 모달 저장 성공 후 화면 즉시 반영 처리
  const handleAfterUpdate = (updated: any) => {
    setSelectedRow1(null);
    setSelectedRow2(null);
    setSelectedRow3(null);
    setSelectedRows1([]);
    setSelectedRows2([]);
    setSelectedRows3([]);
    setSelectedRows4([]);
    setTable1Ver(v => v + 1);
    setTable2Ver(v => v + 1);
    setTable3Ver(v => v + 1);
    setTable4Ver(v => v + 1);
    refetch1();
    refetch2();
    refetch3();
    refetch4();
  };

  return (
    <>
      <div className="flex flex-col h-full">
        <SearchBoxLayout>
          <div className="flex items-end mb-3">
            <SelectBox
              label="명칭"
              className="w-25"
              value={searchType}
              onChange={v => setSearchType(v as "parent" | "child")}
              options={[
                { label: "대분류", value: "parent" },
                { label: "소분류", value: "child" },
              ]}
            />
            <div className="mr-1" />
            <InputBox
              className="w-50"
              value={searchValue}
              onChange={e => setSearchValue(e.target.value)}
              onKeyDown={e => {
                if (e.key === "Enter") {
                  e.preventDefault();
                  handleSearch();
                }
              }}
            />

            <div className="flex gap-[6px] ml-auto">
              <Button variant="reset" onClick={handleReset}>
                초기화
              </Button>
              <Button variant="search" onClick={handleSearch} disabled={searchLoading}>
                {searchLoading ? "검색 중..." : "조회"}
              </Button>
            </div>
          </div>
        </SearchBoxLayout>
        <main className="flex-1 px-4 flex flex-col min-h-0">
          <div className="flex items-center justify-end mb-2">
            {/* 버튼들 */}
            <div className="flex gap-2">
              <PermissionButton
                permission="canCreate"
                variant="etc"
                onClick={() => {
                  setSelectedRows1([]);
                  setSelectedRows2([]);
                  setSelectedRows3([]);
                  setSelectedRows4([]);
                  setSelectedRow1(null);
                  setSelectedRow2(null);
                  setSelectedRow3(null);
                  setTable1Ver(v => v + 1);
                  setTable2Ver(v => v + 1);
                  setTable3Ver(v => v + 1);
                  setTable4Ver(v => v + 1);
                  setTablesVer(v => v + 1);
                  setAddModalOpen(true);
                }}
              >
                추가
              </PermissionButton>
              <PermissionButton permission="canUpdate" variant="etc" onClick={handleUpdateClick}>
                수정
              </PermissionButton>
              <PermissionButton permission="canDelete" variant="etc" onClick={handlerDeleteCode}>
                삭제
              </PermissionButton>
            </div>
          </div>

          {/* 2x2 그리드 레이아웃 */}
          <div key={`tables-v${tablesVer}`} className="flex-1 min-h-0 grid grid-cols-2 grid-rows-2 gap-4">
            {/* 1depth 테이블 (좌상) */}
            <div className="flex flex-col">
              <div className="mb-2">
                <CountBox count={safeData1.length} />
              </div>
              <Table
                key={`${scroll1.tableKey}-v${table1Ver}`}
                {...scroll1.tableProps}
                showCheckbox
                showRowNumber
                resizable
                highlightOnClick
                className="h-full"
                columns={columns1}
                selectedRows={selectedRows1}
                onRowSelect={setSelectedRows1}
                onRowClick={handle1RowClick}
                maxHeight="100%"
              />
            </div>

            {/* 2depth 테이블 (우상) */}
            <div className="flex flex-col">
              <div className="mb-2">
                <CountBox count={safeData2.length} />
              </div>
              <Table
                key={`${scroll2.tableKey}-${parentId1}-v${table2Ver}`}
                {...scroll2.tableProps}
                showCheckbox
                showRowNumber
                resizable
                highlightOnClick
                className="h-full"
                columns={columns2}
                selectedRows={selectedRows2}
                onRowSelect={setSelectedRows2}
                onRowClick={handle2RowClick}
                emptyMessage={parentId1 === undefined ? "1depth를 선택하세요" : undefined}
                maxHeight="100%"
              />
            </div>

            {/* 3depth 테이블 (좌하) */}
            <div className="flex flex-col">
              <div className="mb-2">
                <CountBox count={safeData3.length} />
              </div>
              <Table
                key={`${scroll3.tableKey}-${parentId2}-v${table3Ver}`}
                {...scroll3.tableProps}
                showCheckbox
                showRowNumber
                resizable
                highlightOnClick
                className="h-full"
                columns={columns3}
                selectedRows={selectedRows3}
                onRowSelect={setSelectedRows3}
                onRowClick={handle3RowClick}
                emptyMessage={parentId2 === undefined ? "2depth를 선택하세요" : undefined}
                maxHeight="100%"
              />
            </div>

            {/* 4depth 테이블 (우하) */}
            <div className="flex flex-col">
              <div className="mb-2">
                <CountBox count={safeData4.length} />
              </div>
              <Table
                key={`${scroll4.tableKey}-${parentId3}-v${table4Ver}`}
                {...scroll4.tableProps}
                showCheckbox
                showRowNumber
                resizable
                className="h-full"
                columns={columns4}
                selectedRows={selectedRows4}
                onRowSelect={setSelectedRows4}
                emptyMessage={parentId3 === undefined ? "3depth를 선택하세요" : undefined}
                maxHeight="100%"
              />
            </div>
          </div>
        </main>
      </div>
      <AddModal isOpen={addModalOpen} onClose={() => setAddModalOpen(false)} />
      <UpdateModal
        isOpen={updateModalOpen}
        onClose={() => setUpdateModalOpen(false)}
        selectedData={selectedRowForUpdate}
        onAfterUpdate={handleAfterUpdate}
      />
    </>
  );
}
